import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useInfiniteScrollSentinel } from '@core/hooks/useInfiniteScrollSentinel';
import {
  type PastCombination,
  type PastCombinationPage,
  pastCombinationsInfiniteQuery,
} from '../../api/pastCombination';

/** Offset pages can overlap when items are added between page loads. */
export function flattenPastCombinationPages(
  pages: PastCombinationPage[] = []
): PastCombination[] {
  const seen = new Set<number>();
  return pages
    .flatMap((page) => page.items)
    .filter((combination) => {
      if (seen.has(combination.id)) return false;
      seen.add(combination.id);
      return true;
    });
}

/**
 * Past order combinations for a design, loaded a page at a time as the
 * returned `sentinelRef` (placed after the list) scrolls into view.
 */
export function usePastCombinationPages(designId: number | null) {
  const {
    data,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useInfiniteQuery({
    ...pastCombinationsInfiniteQuery(designId ?? 0),
    enabled: designId != null,
  });

  const combinations = useMemo(
    () => flattenPastCombinationPages(data?.pages),
    [data]
  );

  const sentinelRef = useInfiniteScrollSentinel({
    hasMore: Boolean(hasNextPage) && !isFetchNextPageError,
    loading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  return {
    combinations,
    isPending,
    isError,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    sentinelRef,
  };
}
